import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWorkouts } from '../hooks/useWorkouts';
import { useSteps } from '../hooks/useSteps';
import { useSports } from '../hooks/useSports';
import { useSchedule } from '../hooks/useSchedule';
import { useWater } from '../hooks/useWater';
import { useWeight } from '../hooks/useWeight';
import { useCalories } from '../hooks/useCalories';
import { useSportsData } from '../hooks/useSportsData';
import { useFavoriteSports } from '../hooks/useFavoriteSports';
import { getDefaultSchedule } from '../utils/scheduleData';
import { dataPath } from '../services/dataService';

// ── Lean section — data loaded from /data/lean.json ──────────────────────────

const FALLBACK_LEAN = [
  { id: 'salsa',        category: 'dance', label: 'Salsa',         desc: 'High-energy Latin dance that torches calories and improves coordination', image: '/images/dance/salsa.png',              calories: '400–600 cal/hr', tag: 'CARDIO'   },
  { id: 'lat-pull-down',category: 'gym',   label: 'Lat Pull-Down', desc: 'Build a wide back and upper-body pulling strength',                       image: '/images/gym/back-lap-pull-down.png',   calories: '250–350 cal/hr', tag: 'STRENGTH' },
  { id: 'leg-press',    category: 'gym',   label: 'Leg Press',     desc: 'Isolate quads and glutes with lower spinal load',                         image: '/images/gym/%20LegPress%20.png',       calories: '300–450 cal/hr', tag: 'STRENGTH' },
];

const LEAN_TAG_COLORS = {
  CARDIO:   'bg-pink-100 text-pink-700',
  STRENGTH: 'bg-blue-100 text-blue-700',
  HIIT:     'bg-orange-100 text-orange-700',
  YOGA:     'bg-purple-100 text-purple-700',
  DANCE:    'bg-fuchsia-100 text-fuchsia-700',
};

function LeanSection() {
  const [items, setItems] = useState(FALLBACK_LEAN);

  useEffect(() => {
    fetch(dataPath('leanCollection'))
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (Array.isArray(data) && data.length) setItems(data); })
      .catch(() => {});
  }, []);

  return (
    <section>
      <div className="mb-5">
        <SectionHeader label="GET LEAN" title="Lean Activities" color="#DB2777" />
        <p className="mt-1.5 text-sm text-slate-500">Mix cardio and strength to burn fat and build lean muscle.</p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map(item => {
          const tagClass = LEAN_TAG_COLORS[item.tag] ?? 'bg-slate-100 text-slate-600';
          return (
            <div
              key={item.id}
              className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="relative h-48 bg-slate-100">
                <img
                  src={item.image}
                  alt={item.label}
                  className="h-full w-full object-cover"
                  onError={e => { e.currentTarget.style.display = 'none'; }}
                />
                {item.tag && (
                  <span className={`absolute left-3 top-3 rounded-full px-2.5 py-0.5 text-xs font-bold ${tagClass}`}>
                    {item.tag}
                  </span>
                )}
              </div>
              <div className="p-4">
                <p className="font-black text-slate-900">{item.label}</p>
                {item.desc && <p className="mt-1 text-xs leading-relaxed text-slate-500">{item.desc}</p>}
                {item.calories && (
                  <p className="mt-2 text-xs font-semibold text-emerald-600">🔥 {item.calories}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── Promo carousel — data loaded from /data/promos.json ──────────────────────

const FALLBACK_PROMOS = [
  { id: 'ahhshhh',     tag: 'FEATURED APP', title: 'Deep Focus with Ahhshhh',    desc: 'Guided meditation & yoga for a calmer, sharper mind. Try it free.',                           emoji: '🧘', url: 'https://ahhshhh.life/auth', cta: 'Explore Now →', gradient: ['#4F46E5', '#A855F7'] },
  { id: 'new-sports',  tag: "WHAT'S NEW",   title: 'New Sports Unlocked',          desc: 'Muay Thai, BJJ, Butterfly stroke, Mountain Biking & 10 more disciplines added.',             emoji: '🥊', url: null, cta: null, gradient: ['#059669', '#0D9488'] },
  { id: 'calorie-burn',tag: 'FEATURE',      title: 'Calorie Burn Tracker',         desc: 'Set your session duration and see live estimated calories burned — using established MET formulas.',       emoji: '🔥', url: null, cta: null, gradient: ['#EA580C', '#DC2626'] },
  { id: 'analytics',   tag: 'ANALYTICS',    title: 'Nutrition & Burn Insights',    desc: 'Analytics now shows calories burned per sport and macro breakdown from food logs.',           emoji: '📊', url: null, cta: null, gradient: ['#7C3AED', '#4F46E5'] },
];

function slideGradient(slide) {
  const [c1, c2] = slide.gradient ?? ['#4F46E5', '#7C3AED'];
  return `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`;
}

function PromoCarousel() {
  const [slides, setSlides] = useState(FALLBACK_PROMOS);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  // Load from server — no redeploy needed to update slides
  useEffect(() => {
    fetch(dataPath('promos'))
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (Array.isArray(data) && data.length) setSlides(data); })
      .catch(() => {});
  }, []);

  const total = slides.length;
  const next = useCallback(() => setActive(i => (i + 1) % total), [total]);
  const prev = useCallback(() => setActive(i => (i - 1 + total) % total), [total]);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [paused, next]);

  // Clamp active index in case slide count changes after fetch
  const idx = Math.min(active, slides.length - 1);
  const slide = slides[idx];

  return (
    <div
      className="relative overflow-hidden rounded-2xl shadow-lg select-none"
      style={{ background: slideGradient(slide), transition: 'background 0.6s ease' }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Slide content */}
      <div className="px-6 py-6 sm:px-8 sm:py-7">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center gap-2">
              <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-bold text-white">
                {slide.tag}
              </span>
            </div>
            <h3 className="text-xl font-black text-white leading-tight mb-1">{slide.title}</h3>
            <p className="text-sm text-white/80 leading-relaxed mb-4">{slide.desc}</p>
            {slide.url && slide.cta && (
              <a
                href={slide.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-bold shadow-md transition hover:scale-105 hover:shadow-lg"
                style={{ color: slide.gradient?.[0] ?? '#4F46E5' }}
              >
                {slide.cta}
              </a>
            )}
          </div>
          <div className="shrink-0 text-5xl sm:text-6xl" style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.2))' }}>
            {slide.emoji}
          </div>
        </div>
      </div>

      {/* Prev / Next arrows */}
      <button
        onClick={prev}
        className="absolute left-2 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition hover:bg-white/35"
        aria-label="Previous"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        onClick={next}
        className="absolute right-2 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition hover:bg-white/35"
        aria-label="Next"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Dot indicators */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className={`rounded-full transition-all duration-300 ${i === idx ? 'w-5 h-2 bg-white' : 'w-2 h-2 bg-white/40'}`}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

const activities = [
  { type: 'walking',   label: 'Walking',   icon: '🚶', desc: 'Live tracker + step sync', from: 'from-emerald-500', to: 'to-green-700', route: '/activities/walking' },
  { type: 'gym',       label: 'Gym',        icon: '🏋️', desc: 'Log strength sessions',  from: 'from-blue-500',    to: 'to-blue-700'    },
  { type: 'badminton', label: 'Badminton',  icon: '🏸', desc: 'Record court sessions',   from: 'from-orange-500',  to: 'to-orange-700'  },
  { type: 'skating',   label: 'Skating',    icon: '⛸️', desc: 'Log skating distance',    from: 'from-cyan-500',    to: 'to-teal-700'    },
];

const tips = [
  { icon: '🎯', title: 'Consistent Logging',  desc: 'Log every session, even short ones. Consistency in tracking builds consistency in training.' },
  { icon: '📊', title: 'Track Your Progress', desc: 'Review weekly totals and trends to see how your fitness improves over time.' },
  { icon: '🔄', title: 'Mix Activities',      desc: 'Balance different activities to build overall fitness and prevent burnout.' },
];

const GURU_TIPS = [
  { icon: '💧', title: 'Hydration is King',     desc: 'Drink 8–10 glasses of water daily. During exercise, sip every 15–20 minutes to maintain performance.' },
  { icon: '😴', title: 'Sleep to Grow',          desc: 'Muscles repair during deep sleep. Aim for 7–9 hours. Poor sleep undermines gains and kills motivation.' },
  { icon: '🥗', title: 'Fuel Right',             desc: 'Eat complex carbs before workouts for energy. Have protein within 30 min post-workout for muscle repair.' },
  { icon: '📅', title: 'Plan Your Week',         desc: 'Schedule workouts like important meetings. Written plans are followed 3× more than unplanned intentions.' },
  { icon: '🔄', title: 'Progressive Overload',   desc: 'Gradually increase weight, reps, or duration each week. Your body adapts — keep challenging it.' },
  { icon: '🧘', title: 'Active Recovery',        desc: 'Light walks or stretching on rest days beats complete inactivity for muscle recovery and soreness.' },
];

function GuruWisdomSection({ guruIcons }) {
  const icons = (guruIcons && guruIcons.length === GURU_TIPS.length) ? guruIcons : GURU_TIPS.map(t => t.icon);
  return (
    <section className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-amber-50 p-6 md:p-8">
        <p className="mb-1 text-[10px] font-black uppercase tracking-widest text-orange-600">From the Guru</p>
        <h2 className="text-2xl font-black text-slate-900">Fitness Wisdom</h2>
        <p className="mt-1 text-sm text-slate-500">Training principles that separate good athletes from great ones.</p>
      </div>
      <div className="divide-y divide-slate-100">
        {GURU_TIPS.map((tip, i) => (
          <div key={tip.title} className="flex items-start gap-4 p-5 md:px-8">
            <span className="shrink-0 text-2xl leading-none">{icons[i]}</span>
            <div>
              <p className="font-black text-slate-900">{tip.title}</p>
              <p className="mt-1 text-sm leading-relaxed text-slate-500">{tip.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const activityMeta = {
  walking:     { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: '🚶' },
  gym:         { bg: 'bg-blue-100',    text: 'text-blue-700',    icon: '🏋️' },
  badminton:   { bg: 'bg-orange-100',  text: 'text-orange-700',  icon: '🏸' },
  skating:     { bg: 'bg-cyan-100',    text: 'text-cyan-700',    icon: '⛸️' },
  running:     { bg: 'bg-emerald-50',  text: 'text-emerald-600', icon: '🏃' },
  cycling:     { bg: 'bg-blue-50',     text: 'text-blue-600',    icon: '🚴' },
  swimming:    { bg: 'bg-sky-100',     text: 'text-sky-700',     icon: '🏊' },
  boxing:      { bg: 'bg-red-100',     text: 'text-red-700',     icon: '🥊' },
  cricket:     { bg: 'bg-amber-100',   text: 'text-amber-700',   icon: '🏏' },
  dance:       { bg: 'bg-pink-100',    text: 'text-pink-700',    icon: '💃' },
  football:    { bg: 'bg-green-100',   text: 'text-green-700',   icon: '⚽' },
  basketball:  { bg: 'bg-orange-50',   text: 'text-orange-600',  icon: '🏀' },
  tennis:      { bg: 'bg-lime-100',    text: 'text-lime-700',    icon: '🎾' },
  tabletennis: { bg: 'bg-teal-100',    text: 'text-teal-700',    icon: '🏓' },
  volleyball:  { bg: 'bg-yellow-100',  text: 'text-yellow-700',  icon: '🏐' },
  hiking:      { bg: 'bg-stone-100',   text: 'text-stone-700',   icon: '🥾' },
  martialarts: { bg: 'bg-red-50',      text: 'text-red-800',     icon: '🥋' },
  yoga:        { bg: 'bg-purple-100',  text: 'text-purple-700',  icon: '🧘' },
  meditation:  { bg: 'bg-indigo-100',  text: 'text-indigo-700',  icon: '🙏' },
};

function formatDuration(mins) {
  if (!mins) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function formatDate() {
  return new Date().toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long'
  });
}

function getTodayDayKey() {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[new Date().getDay()];
}

function SectionHeader({ label, title, color }) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-black uppercase tracking-widest" style={{ color }}>{label}</p>
      <h2 className="text-2xl font-black text-slate-900">{title}</h2>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { workouts, weekWorkouts, totalDistance: totalKm, currentStreak, longestStreak } = useWorkouts();
  const { todaySteps, stepGoal } = useSteps();
  const { upcoming } = useSports();
  const { schedule, completions } = useSchedule();
  const { glasses, goal: waterGoal } = useWater();
  const { todayWeight, unit: weightUnit } = useWeight();
  const { totalCalories, goal: calGoal, progress: calProgress } = useCalories();
  const { index: sportsIndex } = useSportsData();
  const guruIcons = sportsIndex?.guruTipIcons ?? [];
  const { favorites } = useFavoriteSports();

  const favCats = (sportsIndex?.categories ?? []).filter(c => favorites.includes(c.id));

  const allWorkouts = workouts;
  const recentWorkouts = allWorkouts.slice(0, 5);

  const todayKey = getTodayDayKey();
  const stepPct = Math.min(Math.round((todaySteps / stepGoal) * 100), 100);
  const waterPct = Math.min(Math.round((glasses / waterGoal) * 100), 100);
  const nextSport = upcoming[0];
  const todaySchedule = (() => {
    const todayDone = completions[todayKey] || [];
    const day = (schedule && schedule[todayKey]) ? schedule[todayKey] : getDefaultSchedule()[todayKey];
    return { muscles: day.muscles, total: day.exercises.length, done: todayDone.length };
  })();

  const stats = [
    { value: allWorkouts.length,         label: 'Total Sessions', icon: '🏃', color: 'text-emerald-700' },
    { value: weekWorkouts.length,        label: 'This Week',      icon: '📅', color: 'text-blue-700' },
    { value: `${totalKm.toFixed(1)} km`, label: 'Total Distance', icon: '📍', color: 'text-violet-700' },
    { value: currentStreak > 0 ? `${currentStreak}🔥` : '—', label: 'Day Streak', icon: '🔥', color: 'text-orange-600' },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#F8FAFC' }}>

      {/* Hero / Greeting */}
      <section
        className="border-b border-slate-100"
        style={{ background: 'linear-gradient(135deg, #ECFDF5 0%, #F8FAFC 50%, #EFF6FF 100%)' }}
      >
        <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-3xl font-black text-slate-900">{greeting()} 👋</p>
              <p className="mt-1 text-sm text-slate-500">{formatDate()}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/log')}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-emerald-600/25 transition hover:-translate-y-0.5 hover:shadow-emerald-600/40"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Log Workout
              </button>
              <button
                onClick={() => navigate('/history')}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-bold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
              >
                View History
              </button>
            </div>
          </div>

          {/* Stats strip */}
          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label} className="flex flex-col rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
                <span className="text-xl leading-none">{s.icon}</span>
                <span className={`mt-2 text-2xl font-black ${s.color}`}>{s.value}</span>
                <span className="mt-0.5 text-xs font-semibold text-slate-500">{s.label}</span>
              </div>
            ))}
          </div>

          {/* Streak motivation */}
          {currentStreak >= 3 && (
            <div className="mt-4 flex items-center gap-3 rounded-2xl border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 px-5 py-3">
              <span className="text-2xl">🔥</span>
              <div>
                <p className="text-sm font-black text-orange-800">{currentStreak}-day streak! Keep it up!</p>
                <p className="text-xs text-orange-600">Best: {longestStreak} days — {currentStreak >= longestStreak ? "you're at your record!" : `${longestStreak - currentStreak} more to beat your best`}</p>
              </div>
            </div>
          )}
        </div>
      </section>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8 space-y-14">

        {/* Promo / Updates carousel */}
        <PromoCarousel />

        {/* Lean Activities */}
        <LeanSection />

        {/* Today at a Glance */}
        <section>
          <div className="mb-5">
            <SectionHeader label="TODAY" title="Today's Progress" color="#0EA5E9" />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {/* Steps */}
            <button
              onClick={() => navigate('/steps')}
              className="group rounded-2xl border border-slate-100 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="flex items-center justify-between">
                <span className="text-2xl">👟</span>
                <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">{stepPct}%</span>
              </div>
              <p className="mt-3 text-2xl font-black text-slate-900">{todaySteps.toLocaleString()}</p>
              <p className="text-xs text-slate-500">of {stepGoal.toLocaleString()} steps</p>
              <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-emerald-100">
                <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500" style={{ width: `${stepPct}%` }} />
              </div>
            </button>

            {/* Water */}
            <button
              onClick={() => navigate('/water')}
              className="group rounded-2xl border border-slate-100 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="flex items-center justify-between">
                <span className="text-2xl">💧</span>
                <span className="rounded-full bg-sky-50 px-2.5 py-1 text-xs font-bold text-sky-700">{waterPct}%</span>
              </div>
              <p className="mt-3 text-2xl font-black text-slate-900">{glasses}</p>
              <p className="text-xs text-slate-500">of {waterGoal} glasses</p>
              <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-sky-100">
                <div className="h-full rounded-full bg-gradient-to-r from-sky-500 to-blue-500 transition-all duration-500" style={{ width: `${waterPct}%` }} />
              </div>
            </button>

            {/* Calories */}
            <button
              onClick={() => navigate('/calories')}
              className="group rounded-2xl border border-slate-100 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="flex items-center justify-between">
                <span className="text-2xl">🥗</span>
                <span className="rounded-full bg-orange-50 px-2.5 py-1 text-xs font-bold text-orange-700">
                  {Math.min(Math.round(calProgress * 100), 100)}%
                </span>
              </div>
              <p className="mt-3 text-2xl font-black text-slate-900">{totalCalories}</p>
              <p className="text-xs text-slate-500">of {calGoal} kcal</p>
              <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-orange-100">
                <div className="h-full rounded-full bg-gradient-to-r from-orange-500 to-amber-500 transition-all duration-500" style={{ width: `${Math.min(calProgress * 100, 100)}%` }} />
              </div>
            </button>
          </div>
        </section>

        {/* My Sports */}
        <section>
          <div className="mb-5 flex items-center justify-between">
            <SectionHeader label="MY SPORTS" title="Your Favorites" color="#DB2777" />
            <button
              onClick={() => navigate('/my-sports')}
              className="rounded-lg border border-pink-200 bg-pink-50 px-3 py-1.5 text-xs font-bold text-pink-600 transition hover:bg-pink-100"
            >
              Edit
            </button>
          </div>

          {favCats.length === 0 ? (
            <button
              onClick={() => navigate('/my-sports')}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-pink-200 bg-white p-4 text-sm font-semibold text-pink-500 transition hover:bg-pink-50"
            >
              <span className="text-xl">🏅</span>
              Tap to pick your favorite sports
            </button>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-1">
              {favCats.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => navigate('/sports')}
                  className="flex shrink-0 flex-col items-center gap-1.5 rounded-2xl border px-4 py-3 transition hover:-translate-y-0.5 hover:shadow-md"
                  style={{
                    backgroundColor: cat.color + '1A',
                    borderColor: cat.color + '40',
                  }}
                >
                  <span className="text-2xl leading-none">{cat.icon}</span>
                  <span
                    className="text-[10px] font-bold"
                    style={{ color: cat.color }}
                  >
                    {cat.label}
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Quick Log */}
        <section>
          <div className="mb-5">
            <SectionHeader label="QUICK START" title="Log an Activity" color="#059669" />
            <p className="mt-1.5 text-sm text-slate-500">Pick your activity and log today's session.</p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {activities.map((item) => (
              <button
                key={item.type}
                onClick={() => navigate(item.route || `/log?type=${item.type}`)}
                className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br ${item.from} ${item.to} p-5 text-left shadow-md transition-all duration-200 hover:-translate-y-1.5 hover:shadow-xl`}
              >
                <div className="absolute inset-0 bg-black/0 transition-colors duration-200 group-hover:bg-black/8" />
                <div className="relative">
                  <div className="mb-3 text-3xl leading-none">{item.icon}</div>
                  <p className="text-sm font-black text-white">{item.label}</p>
                  <p className="mt-1 text-xs leading-relaxed text-white/70">{item.desc}</p>
                  <div className="mt-4 flex items-center gap-1 text-xs font-bold text-white/0 transition-all duration-200 group-hover:text-white/90">
                    {item.route ? 'Track now' : 'Log now'}
                    <svg className="h-3 w-3 translate-x-0 transition-transform duration-200 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* Fitness Hub */}
        <section>
          <div className="mb-5">
            <SectionHeader label={t('fitnessHub.sectionLabel')} title={t('fitnessHub.title')} color="#7C3AED" />
            <p className="mt-1.5 text-sm text-slate-500">{t('fitnessHub.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {/* Gym Schedule */}
            <button
              onClick={() => navigate('/schedule')}
              className="group rounded-2xl border border-slate-100 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="mb-3 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-xl">📅</span>
                <p className="font-black text-blue-700">{t('fitnessHub.gymScheduleCard')}</p>
              </div>
              <p className="mb-3 text-xs text-slate-500">
                Today: <strong className="text-slate-700">{todaySchedule.muscles.join(', ')}</strong>
              </p>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-blue-100">
                <div
                  className="h-full rounded-full bg-blue-500 transition-all duration-500"
                  style={{ width: `${todaySchedule.total > 0 ? Math.round((todaySchedule.done / todaySchedule.total) * 100) : 0}%` }}
                />
              </div>
              <p className="mt-2 text-xs font-semibold text-blue-600">
                {todaySchedule.done}/{todaySchedule.total} {t('fitnessHub.exercisesDone')}
              </p>
              <p className="mt-3 flex items-center gap-1 text-xs font-bold text-blue-600 opacity-0 transition group-hover:opacity-100">
                {t('fitnessHub.openSchedule')}
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </p>
            </button>

            {/* Step Tracker */}
            <button
              onClick={() => navigate('/steps')}
              className="group rounded-2xl border border-slate-100 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="mb-3 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-xl">👟</span>
                <p className="font-black text-emerald-700">{t('fitnessHub.stepTrackerCard')}</p>
              </div>
              <p className="mb-1 text-2xl font-black text-slate-900">{todaySteps.toLocaleString()}</p>
              <p className="mb-3 text-xs text-slate-500">of {stepGoal.toLocaleString()} {t('steps.goal').toLowerCase()}</p>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-emerald-100">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500"
                  style={{ width: `${stepPct}%` }}
                />
              </div>
              <p className="mt-2 text-xs font-semibold text-emerald-600">{stepPct}% {t('fitnessHub.ofGoal')}</p>
              <p className="mt-3 flex items-center gap-1 text-xs font-bold text-emerald-600 opacity-0 transition group-hover:opacity-100">
                {t('fitnessHub.logSteps')}
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </p>
            </button>

            {/* Sports Hub */}
            <button
              onClick={() => navigate('/sports')}
              className="group rounded-2xl border border-slate-100 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="mb-3 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 text-xl">🏅</span>
                <p className="font-black text-orange-700">{t('fitnessHub.sportsHubCard')}</p>
              </div>
              {nextSport ? (
                <>
                  <p className="text-xs text-slate-500">{t('fitnessHub.nextSession')}</p>
                  <p className="mt-0.5 font-black text-slate-800">{nextSport.sportIcon} {nextSport.sportLabel}</p>
                  <p className="text-xs text-slate-500">
                    {new Date(nextSport.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-xs text-slate-500">{t('fitnessHub.sportsSub')}</p>
                  <p className="mt-0.5 text-sm font-semibold text-slate-700">Skating · Cricket · Dance · Boxing…</p>
                </>
              )}
              <p className="mt-3 flex items-center gap-1 text-xs font-bold text-orange-600 opacity-0 transition group-hover:opacity-100">
                {t('fitnessHub.exploreSports')}
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </p>
            </button>
          </div>
        </section>

        {/* Health Hub */}
        <section>
          <div className="mb-5">
            <SectionHeader label="HEALTH HUB" title="Track Your Health" color="#0EA5E9" />
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {/* Water */}
            <button onClick={() => navigate('/water')}
              className="group rounded-2xl border border-sky-100 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-md"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-100 text-xl">💧</div>
              <p className="mt-3 text-xl font-black text-slate-900">{glasses}<span className="text-sm font-semibold text-slate-400">/{waterGoal}</span></p>
              <p className="text-xs font-semibold text-slate-500">glasses today</p>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-sky-100">
                <div className="h-full rounded-full bg-gradient-to-r from-sky-500 to-cyan-400 transition-all duration-500" style={{ width: `${waterPct}%` }} />
              </div>
            </button>

            {/* Weight */}
            <button onClick={() => navigate('/weight')}
              className="group rounded-2xl border border-violet-100 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-violet-300 hover:shadow-md"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 text-xl">⚖️</div>
              {todayWeight ? (
                <>
                  <p className="mt-3 text-xl font-black text-slate-900">{todayWeight}<span className="text-sm font-semibold text-slate-400"> {weightUnit}</span></p>
                  <p className="text-xs font-semibold text-violet-600">logged today</p>
                </>
              ) : (
                <>
                  <p className="mt-3 text-sm font-bold text-slate-400">Not logged</p>
                  <p className="text-xs text-slate-400">tap to add</p>
                </>
              )}
            </button>

            {/* Calories */}
            <button onClick={() => navigate('/calories')}
              className="group rounded-2xl border border-orange-100 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-orange-300 hover:shadow-md"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 text-xl">🥗</div>
              <p className="mt-3 text-xl font-black text-slate-900">{totalCalories}<span className="text-sm font-semibold text-slate-400">/{calGoal}</span></p>
              <p className="text-xs font-semibold text-slate-500">kcal today</p>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-orange-100">
                <div className="h-full rounded-full bg-gradient-to-r from-orange-500 to-amber-400 transition-all duration-500" style={{ width: `${Math.min(calProgress * 100, 100)}%` }} />
              </div>
            </button>

            {/* Sleep */}
            <button onClick={() => navigate('/sleep')}
              className="group rounded-2xl border border-violet-100 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-violet-300 hover:shadow-md"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-xl">😴</div>
              <p className="mt-3 text-sm font-black text-slate-700">Sleep Tracker</p>
              <p className="mt-1 text-xs text-slate-400">Log hours &amp; quality</p>
            </button>

            {/* BMI */}
            <button onClick={() => navigate('/bmi')}
              className="group rounded-2xl border border-emerald-100 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-md"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-xl">📏</div>
              <p className="mt-3 text-sm font-black text-slate-700">BMI Calculator</p>
              <p className="mt-1 text-xs text-slate-400">Check your BMI</p>
            </button>

            {/* Meal Planner */}
            <button onClick={() => navigate('/meals')}
              className="group rounded-2xl border border-emerald-100 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-md"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-xl">🍽️</div>
              <p className="mt-3 text-sm font-black text-slate-700">Meal Planner</p>
              <p className="mt-1 text-xs text-slate-400">Plan meals &amp; snacks</p>
            </button>

            {/* Timer */}
            <button onClick={() => navigate('/timer')}
              className="group rounded-2xl border border-slate-100 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-xl">⏱️</div>
              <p className="mt-3 text-sm font-black text-slate-700">Interval Timer</p>
              <p className="mt-1 text-xs text-slate-400">Start a session</p>
            </button>
          </div>
        </section>

        {/* Recent Workouts */}
        <section>
          <div className="mb-5 flex items-center justify-between">
            <SectionHeader label="LATEST" title="Recent Workouts" color="#059669" />
            <button
              onClick={() => navigate('/history')}
              className="text-sm font-bold text-emerald-600 transition hover:text-emerald-700"
            >
              View all →
            </button>
          </div>

          {recentWorkouts.length === 0 ? (
            <div className="rounded-2xl border border-slate-100 bg-white p-10 text-center shadow-sm">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
                <span className="text-3xl">🏃</span>
              </div>
              <p className="font-bold text-slate-700">No workouts yet</p>
              <p className="mt-1 text-sm text-slate-400">Log your first session to get started!</p>
              <button
                onClick={() => navigate('/log')}
                className="mt-5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-emerald-600/25"
              >
                Log First Workout
              </button>
            </div>
          ) : (
            <div className="space-y-2.5">
              {recentWorkouts.map((w) => {
                const meta = activityMeta[w.type] || activityMeta.walking;
                const dur = formatDuration(parseInt(w.duration));
                return (
                  <div
                    key={w.id}
                    className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition hover:shadow-md"
                  >
                    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-lg ${meta.bg}`}>
                      {meta.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-black capitalize ${meta.text}`}>{w.type}</p>
                      <p className="truncate text-xs text-slate-400">
                        {new Date(w.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                        {w.distance ? ` · ${w.distance} km` : ''}
                        {dur ? ` · ${dur}` : ''}
                      </p>
                    </div>
                    {w.notes && (
                      <p className="hidden max-w-[200px] truncate text-xs text-slate-400 sm:block">{w.notes}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Guru Wisdom */}
        <GuruWisdomSection guruIcons={guruIcons} />

        {/* Tips */}
        <section className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm">
          <div className="border-b border-slate-100 p-6 md:p-8">
            <SectionHeader label="TIPS" title="Stay Consistent" color="#059669" />
            <p className="mt-2 text-sm text-slate-500">Simple habits that compound into big fitness gains.</p>
          </div>
          <div className="grid grid-cols-1 divide-y divide-slate-100 md:grid-cols-3 md:divide-x md:divide-y-0">
            {tips.map((f) => (
              <div key={f.title} className="p-6 md:p-8">
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50">
                  <span className="text-xl">{f.icon}</span>
                </div>
                <h3 className="font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

      </main>

      <footer className="border-t border-slate-100 bg-white/80 py-6 text-center backdrop-blur-sm">
        <p className="text-xs text-slate-400">
          Built with purpose · Track every step · &copy; {new Date().getFullYear()} Vivifyer
        </p>
      </footer>
    </div>
  );
}
